import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdAdd,
  MdBlock,
  MdCheckCircle,
  MdPerson,
  MdEmail,
  MdPhone,
  MdWork,
} from "react-icons/md";
import { createApi } from "../../../../core/api";
import { Switch } from "../../../components/SystemComponent/Switch";

export const CompanyUserByCompany = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();
  const { companyId } = useParams();
  const cid = Number(companyId);

  const [data, setData] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState("");

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [roleId, setRoleId] = useState<number | "">("");

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const [cu, r, c] = await Promise.all([
        api.companyUser.getByCompanyId(cid),
        api.Role.getAll(),
        api.company.getById(cid),
      ]);

      if (cu?.ok) {
        setData(cu.data?.data ?? cu.data ?? []);
        setCompanyName(c.data?.data.companyName);
      }

      if (r?.ok) {
        setRoles(r.data?.data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [api, cid]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Company Users",
        items: [
          { label: "Company Users", route: `/dashboard/company/${cid}/users` },
        ],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus, cid]);

  const toggleBlockUser = async (userId: number) => {
    await api.user.setBlockUnblock(userId);
    load();
  };

  const submit = async () => {
    if (
      !username ||
      !email ||
      !password ||
      !firstName ||
      !lastName ||
      !roleId ||
      saving
    )
      return;

    setSaving(true);
    setError(null);
    try {
      const r = await api.company.userCreate({
        companyId: cid,
        user: { username, email, password, firstName, lastName, roleId },
      });
      if (!r?.ok) throw new Error("Create failed");
      setOpen(false);
      setUsername("");
      setEmail("");
      setPassword("");
      setFirstName("");
      setLastName("");
      setRoleId("");
      load();
    } catch (e: any) {
      setError(e?.message || "Unexpected error");
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "user",
        title: "User",
        minWidth: 320,
        render: (r: any) => {
          if (!r || !r.user) return null;

          const u = r.user;
          const isBlocked = u.isDeleted;
          const mediaUrl =
            typeof u.profileMedia?.url === "string" ? u.profileMedia.url : null;
          const avatar = u.username?.charAt(0)?.toUpperCase() ?? "?";

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {mediaUrl ? (
                <img
                  src={mediaUrl}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    objectFit: "cover",
                    opacity: isBlocked ? 0.4 : 1,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: "#E5E7EB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    opacity: isBlocked ? 0.4 : 1,
                  }}
                >
                  {avatar}
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column" }}>
                <Badge
                  icon={MdPerson}
                  label={`${u.firstName} ${u.lastName}`}
                  color={isBlocked ? "#DC2626" : "#22C55E"}
                  bg={isBlocked ? "rgba(220,38,38,.15)" : "rgba(34,197,94,.15)"}
                />
              </div>
            </div>
          );
        },
      },

      {
        key: "email",
        title: "Email",
        minWidth: 260,
        render: (r: any) =>
          r?.user?.email ? (
            <Badge
              icon={MdEmail}
              label={r.user.email}
              color="#2563EB"
              bg="rgba(37,99,235,.1)"
            />
          ) : (
            "-"
          ),
      },
      {
        key: "username",
        title: "Username",
        minWidth: 260,
        render: (r: any) =>
          r?.user?.username ? (
            <Badge
              icon={MdPerson}
              label={r.user.username}
              color="#a0979dff"
              bg="rgba(37,99,235,.1)"
            />
          ) : (
            "-"
          ),
      },

      {
        key: "phone",
        title: "Phone",
        minWidth: 200,
        render: (r: any) =>
          r?.user?.phoneNumber ? (
            <Badge
              icon={MdPhone}
              label={r.user.phoneNumber}
              color="#0F766E"
              bg="rgba(15,118,110,.1)"
            />
          ) : (
            "-"
          ),
      },

      {
        key: "officePhone",
        title: "Office Phone",
        minWidth: 200,
        render: (r: any) => r?.user?.officePhoneNumber ?? "-",
      },

      {
        key: "role",
        title: "Role",
        minWidth: 180,
        render: (r: any) =>
          r?.user?.role?.name ? (
            <Badge
              icon={MdWork}
              label={String(r.user.role.name).trim()}
              color="#7C3AED"
              bg="rgba(124,58,237,.12)"
            />
          ) : (
            "-"
          ),
      },

      {
        key: "actions",
        title: "Block / Unblock",
        minWidth: 160,
        render: (r: any) => {
          if (!r || !r.user) return null;

          const u = r.user;
          const isBlocked = u.isDeleted;

          return (
            <Switch
              value={!isBlocked}
              onChange={() => toggleBlockUser(u.id)}
              size={44}
            />
          );
        },
      },
    ],
    [navigator],
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}> {companyName} Company Users</div>
          <div style={S.subTitle}>Users assigned to this company</div>
        </div>
        <Button variant="primary" icon={MdAdd} onClick={() => setOpen(true)}>
          Add User
        </Button>
      </div>

      <Table
        tableKey="company-users-by-company"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>Add Company User</div>
        <div style={S.form}>
          <input
            style={S.input}
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
          />
          <input
            style={S.input}
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            style={S.input}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <input
            style={S.input}
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <input
            style={S.input}
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <select
            style={S.input}
            value={roleId}
            onChange={(e) => setRoleId(Number(e.target.value))}
          >
            <option value="">Select role</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          {error && <div style={S.error}>{error}</div>}
        </div>
        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={saving}>
            Save
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: { position: "absolute", inset: 0, padding: 20, background: "#F8FAFC" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 600, color: "#0F172A" },
  subTitle: { fontSize: 13, color: "#64748B" },
  modalTitle: { fontSize: 18, fontWeight: 800, marginBottom: 14 },
  form: { display: "flex", flexDirection: "column", gap: 10 },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: { fontSize: 12, fontWeight: 600, color: "#DC2626" },
};
